import React from "react";
import { useNavigate } from "react-router-dom";
import {
  HelpCircle,
  BarChart3,
  TrendingUp,
  PieChart,
  Table,
  Target,
  MessageSquare,
  Lightbulb,
  MapPin,
  Calendar,
  Activity,
  FileText,
  Bot,
  AlertTriangle,
  TrendingDown,
  Filter,
  Download,
  Globe,
  Settings2,
  Layers,
  Send,
  History,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useLanguage } from "@/i18n/LanguageContext";
import { Navbar } from "@/components/layout/Navbar";

export const SupportView: React.FC<{ publicMode?: boolean }> = ({ publicMode = false }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleExampleQuery = (query: string) => {
    navigate(publicMode ? "/embed" : "/", { state: { exampleQuery: query } });
  };

  const chartTypes = [
    {
      icon: BarChart3,
      name: t("support.chart.bar"),
      description: t("support.chart.bar.desc"),
      example: t("support.chart.bar.example"),
    },
    {
      icon: TrendingUp,
      name: t("support.chart.line"),
      description: t("support.chart.line.desc"),
      example: t("support.chart.line.example"),
    },
    {
      icon: PieChart,
      name: t("support.chart.pie"),
      description: t("support.chart.pie.desc"),
      example: t("support.chart.pie.example"),
    },
    {
      icon: Table,
      name: t("support.chart.table"),
      description: t("support.chart.table.desc"),
      example: t("support.chart.table.example"),
    },
    {
      icon: Target,
      name: t("support.chart.kpi"),
      description: t("support.chart.kpi.desc"),
      example: t("support.chart.kpi.example"),
    },
  ];

  const exampleQueries = [
    {
      icon: MapPin,
      category: t("support.cat.regional"),
      queries: [
        t("support.cat.regional.q1"),
        t("support.cat.regional.q2"),
        t("support.cat.regional.q3"),
        t("support.cat.regional.q4"),
      ],
    },
    {
      icon: Calendar,
      category: t("support.cat.time"),
      queries: [
        t("support.cat.time.q1"),
        t("support.cat.time.q2"),
        t("support.cat.time.q3"),
        t("support.cat.time.q4"),
      ],
    },
    {
      icon: Activity,
      category: t("support.cat.indicators"),
      queries: [
        t("support.cat.indicators.q1"),
        t("support.cat.indicators.q2"),
        t("support.cat.indicators.q3"),
        t("support.cat.indicators.q4"),
      ],
    },
  ];

  const tips = [
    {
      title: t("support.tip1.title"),
      description: t("support.tip1.desc"),
    },
    {
      title: t("support.tip2.title"),
      description: t("support.tip2.desc"),
    },
    {
      title: t("support.tip3.title"),
      description: t("support.tip3.desc"),
    },
    {
      title: t("support.tip4.title"),
      description: t("support.tip4.desc"),
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at 0% 0%, rgba(59,130,246,0.05) 0%, transparent 50%), radial-gradient(ellipse at 100% 100%, rgba(59,130,246,0.04) 0%, transparent 50%), #f8fafc' }}>
      <Navbar publicMode={publicMode} />

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="support-documentation space-y-10">
          <section className="support-reveal relative overflow-hidden rounded-3xl border border-primary-100 bg-gradient-to-br from-primary-700 via-primary-600 to-cyan-600 px-6 py-8 text-white shadow-xl shadow-primary-200/40 sm:px-9 sm:py-10">
            <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
            <div className="pointer-events-none absolute -bottom-24 left-1/3 h-52 w-52 rounded-full bg-cyan-300/20 blur-2xl" />
            <Sparkles className="support-float pointer-events-none absolute right-12 top-10 h-7 w-7 text-white/50" />
            <div className="support-orbit pointer-events-none absolute bottom-12 right-28 h-3 w-3 rounded-full bg-cyan-200/80" />
            <div className="relative max-w-2xl">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold tracking-wide">
                <HelpCircle className="h-3.5 w-3.5" />
                {t("support.hero.badge")}
              </div>
              <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{t("support.hero.title")}</h1>
              <p className="mt-3 max-w-xl text-sm leading-6 text-primary-50 sm:text-base">
                {t("support.hero.desc")}
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                <Button
                  onClick={() => navigate(publicMode ? "/embed" : "/")}
                  className="bg-white text-primary-700 hover:bg-primary-50"
                >
                  <MessageSquare className="mr-1.5 h-4 w-4" />
                  {t("support.hero.openChat")}
                </Button>
                <a href="#examples" className="inline-flex items-center rounded-lg border border-white/25 bg-white/10 px-4 py-2 text-xs font-semibold transition hover:bg-white/20">
                  {t("support.hero.browse")}
                </a>
              </div>
            </div>
          </section>

          <section className="grid gap-3 sm:grid-cols-3">
            {[
              { icon: MessageSquare, title: t("support.quick.ask.title"), text: t("support.quick.ask.text") },
              { icon: AlertTriangle, title: t("support.quick.anomaly.title"), text: t("support.quick.anomaly.text") },
              { icon: FileText, title: t("support.quick.report.title"), text: t("support.quick.report.text") },
            ].map((item) => (
              <Card key={item.title} className="support-reveal support-interactive group border-primary-100/70 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600 transition group-hover:bg-primary-100">
                    <item.icon className="h-4 w-4" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-slate-800">{item.title}</h2>
                    <p className="mt-1 text-xs leading-5 text-slate-500">{item.text}</p>
                  </div>
                </div>
              </Card>
            ))}
          </section>

          <section className="grid gap-6 lg:grid-cols-[190px_minmax(0,1fr)]">
            <aside className="hidden lg:block">
              <div className="sticky top-24 border-l border-primary-200 pl-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary-600">{t("support.nav.onThisPage")}</p>
                <nav className="mt-3 space-y-2 text-xs text-slate-500">
                  <a className="block transition hover:text-primary-700" href="#getting-started">{t("support.nav.gettingStarted")}</a>
                  <a className="block transition hover:text-primary-700" href="#chat-guide">{t("support.nav.chat")}</a>
                  <a className="block transition hover:text-primary-700" href="#examples">{t("support.nav.examples")}</a>
                  <a className="block transition hover:text-primary-700" href="#tips">{t("support.nav.tips")}</a>
                </nav>
              </div>
            </aside>
            <div className="min-w-0 space-y-10">

          {/* Getting Started */}
          <section id="getting-started" className="scroll-mt-24">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                {t("support.gettingStarted")}
              </h2>
            </div>

            <Card className="support-reveal support-delay-1 border-primary-100/70 p-6 shadow-sm sm:p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">
                    {t("support.howToUse")}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-semibold text-sm mt-0.5">
                        1
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {t("support.step1.title")}
                        </p>
                        <p className="text-sm text-slate-600">
                          {t("support.step1.desc")}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-semibold text-sm mt-0.5">
                        2
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {t("support.step2.title")}
                        </p>
                        <p className="text-sm text-slate-600">
                          {t("support.step2.desc")}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-semibold text-sm mt-0.5">
                        3
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {t("support.step3.title")}
                        </p>
                        <p className="text-sm text-slate-600">
                          {t("support.step3.desc")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">
                    {t("support.context.title")}
                  </h3>
                  <div className="space-y-3 text-sm text-slate-700">
                    <p>{t("support.context.intro")}</p>
                    <ul className="space-y-1 pl-4">
                      <li>
                        • <strong>{t("support.context.diseases")}</strong>
                      </li>
                      <li>
                        • <strong>{t("support.context.regions")}</strong>
                      </li>
                      <li>
                        • <strong>{t("support.context.facilities")}</strong>
                      </li>
                      <li>
                        • <strong>{t("support.context.indicators")}</strong>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </Card>
          </section>

          {/* Chat Interface */}
          <section id="chat-guide" className="scroll-mt-24">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center">
                <Bot className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                {t("support.chat.title")}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-primary-500" />
                  {t("support.chat.what.title")}
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  {t("support.chat.what.desc")}
                </p>
                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex gap-2">
                    <Send className="w-4 h-4 text-primary-500 mt-0.5 shrink-0" />
                    <span><strong>{t("support.chat.step1.label")}</strong> — {t("support.chat.step1")}</span>
                  </div>
                  <div className="flex gap-2">
                    <Sparkles className="w-4 h-4 text-primary-500 mt-0.5 shrink-0" />
                    <span><strong>{t("support.chat.step2.label")}</strong> — {t("support.chat.step2")}</span>
                  </div>
                  <div className="flex gap-2">
                    <History className="w-4 h-4 text-primary-500 mt-0.5 shrink-0" />
                    <span><strong>{t("support.chat.step3.label")}</strong> — {t("support.chat.step3")}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-primary-500" />
                  {t("support.chat.tips.title")}
                </h3>
                <div className="space-y-3 text-sm text-slate-600">
                  {[
                    { title: t("support.chat.tip1.title"), text: t("support.chat.tip1.text") },
                    { title: t("support.chat.tip2.title"), text: t("support.chat.tip2.text") },
                    { title: t("support.chat.tip3.title"), text: t("support.chat.tip3.text") },
                    { title: t("support.chat.tip4.title"), text: t("support.chat.tip4.text") },
                  ].map((tip) => (
                    <div key={tip.title} className="p-3 bg-primary-50/60 rounded-lg">
                      <p className="font-medium text-primary-700 mb-1">{tip.title}</p>
                      <p>{tip.text}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </section>

          {/* Anomaly Detection */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                {t("support.anomaly.title")}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Filter className="w-4 h-4 text-primary-500" />
                  {t("support.anomaly.sidebar.title")}
                </h3>
                <div className="space-y-2 text-sm text-slate-600">
                  {[
                    { label: t("support.anomaly.sidebar.indicator.label"), text: t("support.anomaly.sidebar.indicator") },
                    { label: t("support.anomaly.sidebar.hierarchy.label"), text: t("support.anomaly.sidebar.hierarchy") },
                    { label: t("support.anomaly.sidebar.run.label"), text: t("support.anomaly.sidebar.run") },
                  ].map((row) => (
                    <div key={row.label} className="flex gap-2">
                      <ChevronRight className="w-4 h-4 text-primary-500 mt-0.5 shrink-0" />
                      <span><strong>{row.label}</strong> {row.text}</span>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-primary-500" />
                  {t("support.anomaly.severity.title")}
                </h3>
                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex gap-2">
                    <span className="inline-block w-3 h-3 rounded-full bg-red-500 mt-1 shrink-0" />
                    <span><strong>{t("support.anomaly.severity.high.label")}</strong> {t("support.anomaly.severity.high")}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="inline-block w-3 h-3 rounded-full bg-amber-500 mt-1 shrink-0" />
                    <span><strong>{t("support.anomaly.severity.medium.label")}</strong> {t("support.anomaly.severity.medium")}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="inline-block w-3 h-3 rounded-full bg-slate-400 mt-1 shrink-0" />
                    <span><strong>{t("support.anomaly.severity.low.label")}</strong> {t("support.anomaly.severity.low")}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary-500" />
                  {t("support.anomaly.types.title")}
                </h3>
                <div className="space-y-2 text-sm text-slate-600">
                  {[
                    { label: t("support.anomaly.types.trend.label"), text: t("support.anomaly.types.trend") },
                    { label: t("support.anomaly.types.rolling.label"), text: t("support.anomaly.types.rolling") },
                    { label: t("support.anomaly.types.seasonal.label"), text: t("support.anomaly.types.seasonal") },
                    { label: t("support.anomaly.types.missing.label"), text: t("support.anomaly.types.missing") },
                  ].map((row) => (
                    <div key={row.label} className="flex gap-2">
                      <ChevronRight className="w-4 h-4 text-primary-500 mt-0.5 shrink-0" />
                      <span><strong>{row.label}</strong> {row.text}</span>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary-500" />
                  {t("support.anomaly.chart.title")}
                </h3>
                <div className="space-y-2 text-sm text-slate-600">
                  <p>{t("support.anomaly.chart.intro")}</p>
                  <div className="flex gap-2">
                    <span className="inline-block w-3 h-3 rounded-full bg-teal-600 mt-1 shrink-0" />
                    <span><strong>{t("support.anomaly.chart.observed.label")}</strong> {t("support.anomaly.chart.observed")}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="inline-block w-3 h-3 rounded-full bg-slate-400 mt-1 shrink-0" />
                    <span><strong>{t("support.anomaly.chart.historical.label")}</strong> {t("support.anomaly.chart.historical")}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="inline-block w-3 h-3 rounded-full bg-blue-600 mt-1 shrink-0" />
                    <span><strong>{t("support.anomaly.chart.rolling.label")}</strong> {t("support.anomaly.chart.rolling")}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="inline-block w-3 h-3 rounded-full bg-violet-600 mt-1 shrink-0" />
                    <span><strong>{t("support.anomaly.chart.seasonal.label")}</strong> {t("support.anomaly.chart.seasonal")}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-primary-500" />
                  {t("support.anomaly.breakdown.title")}
                </h3>
                <div className="space-y-2 text-sm text-slate-600">
                  <p>{t("support.anomaly.breakdown.p1")}</p>
                  <p>{t("support.anomaly.breakdown.p2")}</p>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Filter className="w-4 h-4 text-primary-500" />
                  {t("support.anomaly.filtering.title")}
                </h3>
                <div className="space-y-2 text-sm text-slate-600">
                  {[
                    { label: t("support.anomaly.filtering.scenario.label"), text: t("support.anomaly.filtering.scenario") },
                    { label: t("support.anomaly.filtering.severity.label"), text: t("support.anomaly.filtering.severity") },
                    { label: t("support.anomaly.filtering.table.label"), text: t("support.anomaly.filtering.table") },
                  ].map((row) => (
                    <div key={row.label} className="flex gap-2">
                      <ChevronRight className="w-4 h-4 text-primary-500 mt-0.5 shrink-0" />
                      <span><strong>{row.label}</strong> {row.text}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </section>

          {/* Report Generator */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                {t("support.reports.title")}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-primary-500" />
                  {t("support.reports.template.title")}
                </h3>
                <div className="space-y-2 text-sm text-slate-600">
                  {[
                    { label: t("support.reports.template.yearly.label"), text: t("support.reports.template.yearly.text") },
                    { label: t("support.reports.template.quarterly.label"), text: t("support.reports.template.quarterly.text") },
                    { label: t("support.reports.template.monthly.label"), text: t("support.reports.template.monthly.text") },
                  ].map((row) => (
                    <div key={row.label} className="flex gap-2">
                      <ChevronRight className="w-4 h-4 text-primary-500 mt-0.5 shrink-0" />
                      <span><strong>{row.label}</strong> {row.text}</span>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Settings2 className="w-4 h-4 text-primary-500" />
                  {t("support.reports.params.title")}
                </h3>
                <div className="space-y-2 text-sm text-slate-600">
                  {[
                    { label: t("support.reports.params.period.label"), text: t("support.reports.params.period.text") },
                    { label: t("support.reports.params.geography.label"), text: t("support.reports.params.geography.text") },
                  ].map((row) => (
                    <div key={row.label} className="flex gap-2">
                      <ChevronRight className="w-4 h-4 text-primary-500 mt-0.5 shrink-0" />
                      <span><strong>{row.label}</strong> {row.text}</span>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-primary-500" />
                  {t("support.reports.geography.title")}
                </h3>
                <div className="space-y-2 text-sm text-slate-600">
                  {[
                    { label: t("support.reports.geography.national.label"), text: t("support.reports.geography.national.text") },
                    { label: t("support.reports.geography.regional.label"), text: t("support.reports.geography.regional.text") },
                  ].map((row) => (
                    <div key={row.label} className="flex gap-2">
                      <ChevronRight className="w-4 h-4 text-primary-500 mt-0.5 shrink-0" />
                      <span><strong>{row.label}</strong> {row.text}</span>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Send className="w-4 h-4 text-primary-500" />
                  {t("support.reports.generating.title")}
                </h3>
                <div className="space-y-2 text-sm text-slate-600">
                  {[
                    t("support.reports.generating.s1"),
                    t("support.reports.generating.s2"),
                    t("support.reports.generating.s3"),
                    t("support.reports.generating.s4"),
                  ].map((step, index) => (
                    <div key={step} className="flex gap-2">
                      <span className="inline-block w-5 text-center text-primary-600 font-bold mt-0.5 shrink-0">{index + 1}</span>
                      <span>{step}</span>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <span className="inline-block w-5 text-center text-primary-600 font-bold mt-0.5 shrink-0">5</span>
                    <span>{t("support.reports.generating.s5.label")} — {t("support.reports.generating.s5")}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary-500" />
                  {t("support.reports.improving.title")}
                </h3>
                <div className="space-y-2 text-sm text-slate-600">
                  <p>{t("support.reports.improving.intro")}</p>
                  {[
                    { label: t("support.reports.improving.improve.label"), text: t("support.reports.improving.improve.text") },
                    { label: t("support.reports.improving.regenerate.label"), text: t("support.reports.improving.regenerate.text") },
                  ].map((row) => (
                    <div key={row.label} className="flex gap-2">
                      <ChevronRight className="w-4 h-4 text-primary-500 mt-0.5 shrink-0" />
                      <span><strong>{row.label}</strong> {row.text}</span>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Download className="w-4 h-4 text-primary-500" />
                  {t("support.reports.exporting.title")}
                </h3>
                <div className="space-y-2 text-sm text-slate-600">
                  {[
                    { label: t("support.reports.exporting.docx.label"), text: t("support.reports.exporting.docx") },
                    { label: t("support.reports.exporting.pptx.label"), text: t("support.reports.exporting.pptx") },
                  ].map((row) => (
                    <div key={row.label} className="flex gap-2">
                      <ChevronRight className="w-4 h-4 text-primary-500 mt-0.5 shrink-0" />
                      <span><strong>{row.label}</strong> {row.text}</span>
                    </div>
                  ))}
                  <p className="mt-2">
                    {t("support.reports.exporting.download.label")} — {t("support.reports.exporting.download")}
                  </p>
                </div>
              </Card>
            </div>
          </section>

          {/* Chart Types */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                {t("support.vizTypes")}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {chartTypes.map((chart, index) => (
                <Card
                  key={index}
                  className="support-interactive p-6"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center">
                      <chart.icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 mb-2">
                        {chart.name}
                      </h3>
                      <p className="text-sm text-slate-600 mb-3">
                        {chart.description}
                      </p>
                      <div className="text-xs text-primary-600 font-medium bg-primary-50 px-2 py-1 rounded">
                        {t("support.example.label")} {chart.example}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* Example Queries */}
          <section id="examples" className="scroll-mt-24">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                {t("support.examples")}
              </h2>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {exampleQueries.map((category, index) => (
                <Card key={index} className="support-interactive p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center">
                      <category.icon className="w-4 h-4 text-primary-600" />
                    </div>
                    <h3 className="font-semibold text-slate-900">
                      {category.category}
                    </h3>
                  </div>

                  <div className="space-y-3">
                    {category.queries.map((query, queryIndex) => (
                      <button
                        key={queryIndex}
                        onClick={() => handleExampleQuery(query)}
                        className="w-full text-left p-3 bg-slate-50 hover:bg-primary-50 rounded-lg transition-all duration-200 group hover:translate-x-1"
                      >
                        <p className="text-sm text-slate-700 group-hover:text-primary-700">
                          "{query}"
                        </p>
                      </button>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* Tips */}
          <section id="tips" className="scroll-mt-24">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                {t("support.tips")}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {tips.map((tip, index) => (
                <Card key={index} className="support-interactive p-6">
                  <h3 className="font-semibold text-slate-900 mb-2">
                    {tip.title}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {tip.description}
                  </p>
                </Card>
              ))}
            </div>
          </section>

          {/* Call to Action */}
          <section className="text-center py-12">
            <Card className="p-8 bg-gradient-to-r from-primary-50 to-primary-100">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                {t("support.cta.heading")}
              </h2>
              <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
                {t("support.cta.desc")}
              </p>
              <Button
                onClick={() => navigate(publicMode ? "/embed" : "/")}
                className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800"
              >
                {t("support.cta.btn")}
              </Button>
            </Card>
          </section>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};
